import { useCallback, useMemo } from 'react';
import { normalizePersonName } from '@ast-data/shared';
import { useCrewKeyResolver } from '../projects/crewIdentity';
import {
    storedPersonLinks,
    type StoredPersonLink
} from '../users/personUserId';
import type { DiaryDayPerson } from './useDiaryDay';

/** Split a ", "-joined multi-person value into its trimmed names. */
export function splitPeople(value: string): string[] {
    return value
        ? value
              .split(', ')
              .map((name) => name.trim())
              .filter(Boolean)
        : [];
}

/**
 * Who the Tagebuch already names for the day, and who could still be taken over
 * from what the day actually recorded.
 *
 * People are matched by their CREW KEY, not by their spelling: the same person
 * reaches the day as a free-text name and as a linked user on a Befund. A name
 * the Tagebuch already links to an account is keyed by THAT account — as the
 * day's records are by theirs — so an imported "K.Hecht" and the Fund she
 * recorded are one person, however the name rule would read the spelling.
 */
export function useDiaryCrew({
    excavationLeader,
    technician,
    staff,
    storedLinks,
    dayPeople,
    getStaff,
    setStaff
}: {
    /** The three person fields, as the form currently holds them. */
    excavationLeader: string;
    technician: string;
    staff: string;
    /** The names the stored Tagebuch links to accounts (its `*Id` fields). */
    storedLinks: readonly StoredPersonLink[];
    /** Everyone who recorded something for the project on this day. */
    dayPeople: DiaryDayPerson[];
    getStaff: () => string;
    setStaff: (value: string) => void;
}) {
    const crewResolver = useCrewKeyResolver();
    const linkedIds = useMemo(
        () => storedPersonLinks(storedLinks),
        [storedLinks]
    );
    const keyOf = useCallback(
        (name: string) =>
            crewResolver.keyForName(
                name,
                linkedIds.get(normalizePersonName(name))
            ),
        [crewResolver, linkedIds]
    );

    // Whoever is already named ABOVE, and as what. The day's people list still
    // shows them, but labelled with that role instead of an "add to Personal"
    // button.
    const leadRoleByKey = useMemo(() => {
        const roles = new Map<string, string>();
        for (const name of splitPeople(technician)) {
            const key = keyOf(name);
            if (key) {
                roles.set(key, 'diary.field.technician');
            }
        }
        // Set last, so the Grabungsleiter wins if one person is both.
        const leaderKey = excavationLeader ? keyOf(excavationLeader) : '';
        if (leaderKey) {
            roles.set(leaderKey, 'diary.field.excavationLeader');
        }
        return roles;
    }, [excavationLeader, technician, keyOf]);

    const staffKeys = useMemo(
        () => new Set(splitPeople(staff).map(keyOf)),
        [staff, keyOf]
    );

    // Only people who are neither already Personal nor named above can be taken
    // over — that is what "add all" adds.
    const missingStaff = useMemo(
        () =>
            dayPeople.filter(
                (person) =>
                    !leadRoleByKey.has(person.key) && !staffKeys.has(person.key)
            ),
        [dayPeople, leadRoleByKey, staffKeys]
    );

    return {
        leadRoleByKey,
        staffKeys,
        missingStaff,
        /** Append names to Personal, keeping the order and skipping duplicates. */
        addToStaff(names: string[]) {
            const current = splitPeople(getStaff());
            const taken = new Set(current.map(keyOf));
            const added = names.filter((name) => {
                const key = keyOf(name);
                if (!key || taken.has(key)) {
                    return false;
                }
                taken.add(key);
                return true;
            });
            if (added.length === 0) {
                return;
            }
            setStaff([...current, ...added].join(', '));
        }
    };
}
